import {
  SearchQuery,
  setSearchQuery,
  getSearchQuery,
  findNext,
  findPrevious,
  selectMatches,
  replaceNext,
  replaceAll,
  closeSearchPanel,
} from '@codemirror/search';
import {StateEffect, StateField} from '@codemirror/state';
import {EditorView, runScopeHandlers, type Panel} from '@codemirror/view';

/**
 * StateField that tracks whether the replace row is visible.
 *
 * This field survives `EditorView.setState()` transfers across
 * expand/collapse because `setState()` replaces the entire
 * `EditorState` — including all StateField values. As long as the
 * same StateField definition is present in both the source and
 * target editor configurations, the value carries over intact.
 */
const toggleReplaceVisible = StateEffect.define<boolean>();
const replaceVisibleField = StateField.define<boolean>({
  create: () => false,
  update(value, tr) {
    for (const effect of tr.effects) {
      if (effect.is(toggleReplaceVisible)) return effect.value;
    }
    return value;
  },
});

/** Extension that enables replace-visible persistence across setState transfers. */
export const replaceVisibleExtension = replaceVisibleField;

// SVG icon paths from Spring icon components (16x16 viewBox)
const ICONS = {
  chevronRight:
    'M9.29287 8.00004L6.64642 5.35359L7.35353 4.64648L10.7071 8.00004L7.35353 11.3536L6.64642 10.6465L9.29287 8.00004Z',
  chevronDown:
    'M8.00002 9.29293L10.6465 6.64648L11.3536 7.35359L8.00002 10.7071L4.64647 7.35359L5.35358 6.64648L8.00002 9.29293Z',
  arrowUp:
    'M9.00004 3.70721L12.1465 6.85363L12.8536 6.14652L8.50001 1.79297L4.14645 6.14652L4.85356 6.85363L8.00004 3.70715L8.00004 13.0001H9.00004L9.00004 3.70721Z',
  arrowDown:
    'M8.00001 12.2929L4.85356 9.14645L4.14645 9.85355L8.50001 14.2071L12.8536 9.85355L12.1465 9.14645L9.00001 12.2929L9.00001 3H8.00001L8.00001 12.2929Z',
  close:
    'M8.70711 8.00004L12.3536 4.35359L11.6465 3.64648L8.00001 7.29293L4.35356 3.64648L3.64645 4.35359L7.2929 8.00004L3.64645 11.6465L4.35356 12.3536L8.00001 8.70714L11.6465 12.3536L12.3536 11.6465L8.70711 8.00004Z',
};

function createSvgIcon(pathData: string): SVGElement {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('width', '16');
  svg.setAttribute('height', '16');
  svg.setAttribute('viewBox', '0 0 16 16');
  svg.setAttribute('fill', 'currentColor');
  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  path.setAttribute('d', pathData);
  svg.appendChild(path);
  return svg;
}

function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function elt<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs?: Record<string, string>,
  ...children: (Node | string)[]
): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  if (attrs) {
    for (const [key, value] of Object.entries(attrs)) {
      el.setAttribute(key, value);
    }
  }
  for (const child of children) {
    el.append(child);
  }
  return el;
}

// Search history stored in localStorage for autocomplete in find/replace fields.
const SEARCH_HISTORY_KEY = 'cm-wf-search-history';
const MAX_HISTORY = 20;

function getSearchHistory(): string[] {
  try {
    return JSON.parse(localStorage.getItem(SEARCH_HISTORY_KEY) || '[]');
  } catch {
    return [];
  }
}

function saveToHistory(term: string) {
  if (!term.trim()) return;
  try {
    const history = getSearchHistory().filter((t) => t !== term);
    history.unshift(term);
    if (history.length > MAX_HISTORY) history.length = MAX_HISTORY;
    localStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify(history));
  } catch {
    // localStorage unavailable
  }
}

class WebflowSearchPanel {
  dom: HTMLElement;
  private searchField: HTMLInputElement;
  private replaceField: HTMLInputElement | null = null;
  private searchDatalist: HTMLDataListElement;
  private caseField: HTMLInputElement;
  private reField: HTMLInputElement;
  private wordField: HTMLInputElement;
  private replaceBtn: HTMLButtonElement | null = null;
  private replaceAllBtn: HTMLButtonElement | null = null;
  private replaceRow: HTMLElement | null = null;
  private toggleBtn: HTMLButtonElement | null = null;
  private toggleIcon: SVGElement | null = null;
  private matchCountEl: HTMLSpanElement;

  constructor(private view: EditorView) {
    const isReadOnly = view.state.readOnly;
    const query = getSearchQuery(view.state);
    const phrase = view.state.phrase.bind(view.state);

    // Datalist of previously searched terms for autocomplete
    const datalistId =
      'cm-wf-search-hist-' + Math.random().toString(36).slice(2, 8);
    this.searchDatalist = document.createElement('datalist');
    this.searchDatalist.id = datalistId;
    this.refreshDatalist();

    // Search field
    this.searchField = elt('input', {
      class: 'cm-textfield',
      name: 'search',
      'main-field': 'true',
      placeholder: capitalize(phrase('Find')),
      'aria-label': capitalize(phrase('Find')),
      autocomplete: 'off',
      list: datalistId,
    });
    this.searchField.value = query.search;
    this.searchField.addEventListener('input', () => this.commit());
    this.searchField.addEventListener('keydown', (e) =>
      this.keydown(e, 'search')
    );

    // Checkboxes
    this.caseField = elt('input', {type: 'checkbox', name: 'case'});
    this.caseField.checked = query.caseSensitive;
    this.caseField.addEventListener('change', () => this.commit());

    this.reField = elt('input', {type: 'checkbox', name: 're'});
    this.reField.checked = query.regexp;
    this.reField.addEventListener('change', () => this.commit());

    this.wordField = elt('input', {type: 'checkbox', name: 'word'});
    this.wordField.checked = query.wholeWord;
    this.wordField.addEventListener('change', () => this.commit());

    // Prev/Next icon buttons
    const prevBtn = elt('button', {
      class: 'cm-wf-icon-btn cm-wf-outline',
      name: 'prev',
      'aria-label': capitalize(phrase('previous')),
      type: 'button',
    });
    prevBtn.appendChild(createSvgIcon(ICONS.arrowUp));
    prevBtn.addEventListener('click', () => findPrevious(this.view));

    const nextBtn = elt('button', {
      class: 'cm-wf-icon-btn cm-wf-outline',
      name: 'next',
      'aria-label': capitalize(phrase('next')),
      type: 'button',
    });
    nextBtn.appendChild(createSvgIcon(ICONS.arrowDown));
    nextBtn.addEventListener('click', () => findNext(this.view));

    // Select All button with match counter
    this.matchCountEl = elt('span', {class: 'cm-wf-match-count'});
    const selectAllBtn = elt(
      'button',
      {
        class: 'cm-button cm-wf-outline',
        name: 'select',
        type: 'button',
      },
      capitalize(phrase('All')),
      ' ',
      this.matchCountEl
    );
    selectAllBtn.addEventListener('click', () => selectMatches(this.view));

    // Close button
    const closeBtn = elt('button', {
      class: 'cm-wf-icon-btn cm-wf-ghost',
      name: 'close',
      'aria-label': capitalize(phrase('close')),
      type: 'button',
    });
    closeBtn.appendChild(createSvgIcon(ICONS.close));
    closeBtn.addEventListener('click', () => closeSearchPanel(this.view));

    // Checkbox labels
    const caseLabel = elt(
      'label',
      {},
      this.caseField,
      capitalize(phrase('match case'))
    );
    const reLabel = elt(
      'label',
      {},
      this.reField,
      capitalize(phrase('regexp'))
    );
    const wordLabel = elt(
      'label',
      {},
      this.wordField,
      capitalize(phrase('by word'))
    );

    // Build find row children
    const findRowChildren: (Node | string)[] = [];

    if (!isReadOnly) {
      // Toggle button (expand/collapse replace row)
      this.toggleIcon = createSvgIcon(ICONS.chevronRight);
      this.toggleBtn = elt('button', {
        class: 'cm-wf-icon-btn cm-wf-ghost',
        name: 'toggle',
        'aria-label': 'Toggle replace',
        type: 'button',
      });
      this.toggleBtn.appendChild(this.toggleIcon);
      this.toggleBtn.addEventListener('click', () => this.toggleReplace());
      findRowChildren.push(this.toggleBtn);
    }

    findRowChildren.push(
      this.searchField,
      prevBtn,
      nextBtn,
      selectAllBtn,
      caseLabel,
      reLabel,
      wordLabel,
      closeBtn
    );

    const findRow = elt('div', {class: 'cm-wf-search-row'}, ...findRowChildren);

    // Build the panel DOM
    this.dom = elt('div', {class: 'cm-search cm-wf-search'});

    // Inject autofill overrides into <head> so :-webkit-autofill is respected.
    // Uses both box-shadow inset (covers the autofill bg) and transition delay
    // (prevents the bg change from ever visually completing).
    if (!document.getElementById('cm-wf-autofill-fix')) {
      const style = document.createElement('style');
      style.id = 'cm-wf-autofill-fix';
      const bgColor =
        getComputedStyle(view.dom).getPropertyValue('background-color') ||
        '#333';
      const textColor =
        getComputedStyle(view.contentDOM).getPropertyValue('color') || '#fff';
      style.textContent = [
        `.cm-wf-search .cm-textfield:-webkit-autofill,`,
        `.cm-wf-search .cm-textfield:-webkit-autofill:hover,`,
        `.cm-wf-search .cm-textfield:-webkit-autofill:focus {`,
        `  -webkit-box-shadow: 0 0 0 1000px ${bgColor} inset !important;`,
        `  -webkit-text-fill-color: ${textColor} !important;`,
        `  caret-color: ${textColor} !important;`,
        `  transition: background-color 9999s ease-in-out 0s !important;`,
        `}`,
      ].join('\n');
      document.head.appendChild(style);
    }
    this.dom.addEventListener('keydown', (e) => {
      if (runScopeHandlers(this.view, e, 'search-panel')) {
        e.preventDefault();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        closeSearchPanel(this.view);
        this.view.focus();
      }
    });
    this.dom.appendChild(findRow);

    // Replace row (only for editable editors)
    if (!isReadOnly) {
      this.replaceField = elt('input', {
        class: 'cm-textfield',
        name: 'replace',
        placeholder: capitalize(phrase('Replace')),
        'aria-label': capitalize(phrase('Replace')),
        autocomplete: 'off',
        list: datalistId,
      });
      this.replaceField.value = query.replace;
      this.replaceField.addEventListener('input', () => {
        this.commit();
        this.updateReplaceButtons();
      });
      this.replaceField.addEventListener('keydown', (e) =>
        this.keydown(e, 'replace')
      );

      this.replaceBtn = elt(
        'button',
        {
          class: 'cm-button cm-wf-outline',
          name: 'replace',
          type: 'button',
        },
        capitalize(phrase('replace'))
      ) as HTMLButtonElement;
      this.replaceBtn.disabled = true;
      this.replaceBtn.addEventListener('click', () => replaceNext(this.view));

      this.replaceAllBtn = elt(
        'button',
        {
          class: 'cm-button cm-wf-outline',
          name: 'replaceAll',
          type: 'button',
        },
        capitalize(phrase('replace all'))
      ) as HTMLButtonElement;
      this.replaceAllBtn.disabled = true;
      this.replaceAllBtn.addEventListener('click', () => replaceAll(this.view));

      const spacer = elt('button', {
        class: 'cm-wf-icon-btn cm-wf-ghost cm-wf-toggle-spacer',
        type: 'button',
        'aria-hidden': 'true',
        tabindex: '-1',
      });

      this.replaceRow = elt(
        'div',
        {class: 'cm-wf-search-row cm-wf-replace-row cm-wf-hidden'},
        spacer,
        this.replaceField,
        this.replaceBtn,
        this.replaceAllBtn
      );

      this.dom.appendChild(this.replaceRow);

      // Restore replace row visibility from StateField (survives setState).
      if (view.state.field(replaceVisibleField, false)) {
        this.replaceRow.classList.remove('cm-wf-hidden');
        const icon = createSvgIcon(ICONS.chevronDown);
        this.toggleBtn!.replaceChildren(icon);
        this.toggleIcon = icon;
      }

      // Also listen for input on search field to update replace button state
      this.searchField.addEventListener('input', () =>
        this.updateReplaceButtons()
      );
    }

    // Append datalist to the panel DOM
    this.dom.appendChild(this.searchDatalist);
  }

  private toggleReplace() {
    if (!this.replaceRow || !this.toggleBtn) return;
    const hidden = this.replaceRow.classList.toggle('cm-wf-hidden');
    // Swap chevron icon
    const icon = createSvgIcon(hidden ? ICONS.chevronRight : ICONS.chevronDown);
    this.toggleBtn.replaceChildren(icon);
    this.toggleIcon = icon;
    // Persist visibility in StateField so it survives setState transfers.
    this.view.dispatch({effects: toggleReplaceVisible.of(!hidden)});
    if (!hidden) {
      this.replaceField?.focus();
    }
  }

  private updateReplaceButtons() {
    // Empty replace is valid — it deletes matches (parity with Sublime/VS Code).
    const enabled = this.searchField.value.length > 0;
    if (this.replaceBtn) this.replaceBtn.disabled = !enabled;
    if (this.replaceAllBtn) this.replaceAllBtn.disabled = !enabled;
  }

  private updateMatchCount() {
    const query = getSearchQuery(this.view.state);
    if (!query.search) {
      this.matchCountEl.textContent = '';
      return;
    }
    const iter = query.getCursor(this.view.state);
    let total = 0;
    let currentIndex = 0;
    const {from: selFrom, to: selTo} = this.view.state.selection.main;
    let result = iter.next();
    while (!result.done) {
      total++;
      if (
        currentIndex === 0 &&
        result.value.from === selFrom &&
        result.value.to === selTo
      ) {
        currentIndex = total;
      }
      result = iter.next();
    }
    if (total === 0) {
      this.matchCountEl.textContent = '(0 of 0)';
    } else if (currentIndex === 0) {
      this.matchCountEl.textContent = `(${total})`;
    } else {
      this.matchCountEl.textContent = `(${currentIndex} of ${total})`;
    }
  }

  private commit() {
    const query = new SearchQuery({
      search: this.searchField.value,
      caseSensitive: this.caseField.checked,
      regexp: this.reField.checked,
      wholeWord: this.wordField.checked,
      replace: this.replaceField?.value ?? '',
    });
    if (!query.eq(getSearchQuery(this.view.state))) {
      this.view.dispatch({effects: setSearchQuery.of(query)});
    }
    this.updateMatchCount();
  }

  private keydown(e: KeyboardEvent, field: 'search' | 'replace') {
    if (runScopeHandlers(this.view, e, 'search-panel')) {
      e.preventDefault();
    } else if (e.key === 'Enter' && e.target instanceof HTMLInputElement) {
      e.preventDefault();
      // Save the current value to search history for future autocomplete
      saveToHistory(e.target.value);
      this.refreshDatalist();
      if (field === 'search') {
        if (e.shiftKey) {
          findPrevious(this.view);
        } else {
          findNext(this.view);
        }
      } else {
        replaceNext(this.view);
      }
    }
  }

  /** Rebuild the datalist from saved search history. */
  private refreshDatalist() {
    const history = getSearchHistory();
    this.searchDatalist.textContent = '';
    for (const term of history) {
      const option = document.createElement('option');
      option.value = term;
      this.searchDatalist.appendChild(option);
    }
  }

  /** Sync field values when the query changes externally. */
  private setQuery(query: SearchQuery) {
    if (this.searchField.value !== query.search) {
      this.searchField.value = query.search;
    }
    if (this.replaceField && this.replaceField.value !== query.replace) {
      this.replaceField.value = query.replace;
    }
    if (this.caseField.checked !== query.caseSensitive) {
      this.caseField.checked = query.caseSensitive;
    }
    if (this.reField.checked !== query.regexp) {
      this.reField.checked = query.regexp;
    }
    if (this.wordField.checked !== query.wholeWord) {
      this.wordField.checked = query.wholeWord;
    }
    this.updateReplaceButtons();
  }

  update(update: {
    docChanged?: boolean;
    selectionSet?: boolean;
    transactions: readonly {
      effects: readonly {
        is: (type: typeof setSearchQuery) => boolean;
        value: SearchQuery;
      }[];
    }[];
  }) {
    let queryChanged = false;
    for (const tr of update.transactions) {
      for (const effect of tr.effects) {
        if (effect.is(setSearchQuery)) {
          queryChanged = true;
          if (
            !this.searchField.contains(
              this.view.dom.ownerDocument.activeElement
            )
          ) {
            this.setQuery(effect.value);
          }
        }
      }
    }
    if (queryChanged || update.docChanged || update.selectionSet) {
      this.updateMatchCount();
    }
  }

  mount() {
    this.searchField.select();
    this.updateMatchCount();
  }
}

export function createSearchPanel(view: EditorView): Panel {
  const panel = new WebflowSearchPanel(view);
  return {
    dom: panel.dom,
    top: true,
    mount: () => panel.mount(),
    update: (update) => panel.update(update),
  };
}
